// client/src/api/services/storeService.ts
import { apiClient } from '../client';
import { ENDPOINTS } from '../endpoints';

// ==================== PRODUCT TYPES ====================

export interface Product {
  id: string;
  name: string;
  category: string;
  price: number;
  currency: string;
  stockStatus: 'in_stock' | 'out_of_stock';
  stockAvailable?: boolean;
  rating: number;
  reviewCount: number;
  shortDescription: string;
  fullDescription?: string;
  thumbnailUrl?: string;
  images?: string[];
  details?: { category: string; stockStatus: string };
  isWishlisted: boolean;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface ProductsResponse {
  success: boolean;
  data: { products: Product[]; pagination: Pagination };
}

export interface SingleProductResponse {
  success: boolean;
  data: Product;
}

export interface GetProductsParams {
  category?: string;
  page?: number;
  limit?: number;
  sortBy?: 'newest' | 'price_asc' | 'price_desc' | 'top_rated';
}

export interface ProductsResult {
  products: Product[];
  pagination: Pagination;
}

// ==================== WISHLIST TYPES ====================

export interface WishlistItem {
  id: string;
  productId: string;
  name: string;
  price: number;
  currency: string;
  stockStatus: 'in_stock' | 'out_of_stock';
  rating: number;
  thumbnailUrl: string;
  addedAt: string;
}

export interface WishlistResult {
  items: WishlistItem[];
  totalItems: number;
}

interface WishlistResponse {
  success: boolean;
  data: { items: WishlistItem[]; totalItems: number };
}

interface AddToWishlistResponse {
  success: boolean;
  message?: string;
  error?: string;
  data?: { item?: WishlistItem; totalItems?: number };
}

// ==================== CART TYPES ====================

export interface CartItem {
  id: string;
  productId: string;
  name: string;
  thumbnailUrl?: string;
  unitPrice: number;
  quantity: number;
  lineTotal: number;
  currency: string;
}

export interface CartSummary {
  itemCount: number;
  subtotal: number;
  platformFee: number;
  total: number;
  currency: string;
}

export interface CartResult {
  items: CartItem[];
  summary: CartSummary;
}

export interface AddToCartData { productId: string; quantity: number; }

interface AddToCartResponse {
  success: boolean;
  message?: string;
  data?: { item?: CartItem; cartTotal?: number; cartItemCount?: number };
}

interface CartResponse {
  success: boolean;
  data: { items: CartItem[]; summary: CartSummary };
}

// ==================== ORDER TYPES ====================
// Matches GET /operator/orders and GET /operator/orders/:id API contracts

export type OrderStatus = 'pending' | 'processing' | 'completed' | 'cancelled';

// Lightweight item in orders list
export interface OrderListItem {
  quantity: number;
  unitPrice: number;
  currency: string;
}

// Full item in order detail
export interface OrderDetailItem {
  productId: string;
  name: string;
  thumbnailUrl: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;    // server-computed
  currency: string;
}

export interface OrderSummary {
  subtotal: number;
  itemCount: number;
  platformFee: number;
  total: number;
  currency: string;
}

export interface ActivationTimelineEvent {
  event: string;
  label: string;
  timestamp: string;
  timestampFormatted: string;
  completed: boolean;
}

// Shape from GET /operator/orders (list)
export interface Order {
  id: string;
  orderNumber: string;
  status: OrderStatus;
  placedAt: string;
  placedAtFormatted: string;
  itemCount: number;
  items: OrderListItem[];
  total: number;
  currency: string;
}

// Shape from GET /operator/orders/:id (detail)
export interface OrderDetail {
  id: string;
  orderNumber: string;
  status: OrderStatus;
  placedAt: string;
  placedAtFormatted: string;
  items: OrderDetailItem[];
  summary: OrderSummary;
  paymentMethod: 'wallet' | 'card' | 'crypto';
  paymentStatus: string;
  activationTimeline: ActivationTimelineEvent[];
}

// Shape from POST /operator/checkout/place-order
export interface PlacedOrder {
  orderId: string;
  orderNumber: string;
  status: OrderStatus;
  paymentMethod: string;
  paymentStatus: string;
  total: number;
  currency: string;
  placedAt: string;
  redirectTo: string;
}

// Shape from PATCH /operator/orders/:id/cancel
export interface CancelOrderResult {
  orderId: string;
  orderNumber: string;
  status: 'cancelled';
  cancelledAt: string;
  refundStatus: string;
}

// Internal response wrappers
interface OrdersListResponse  { success: boolean; data: { orders: Order[]; pagination: Pagination }; }
interface OrderDetailResponse { success: boolean; data: OrderDetail; }
interface CancelOrderResponse { success: boolean; message: string; data: CancelOrderResult; }

// ==================== CHECKOUT TYPES ====================

export interface CheckoutOrderItem {
  productId: string;
  name: string;
  thumbnailUrl: string;
  unitPrice: number;
  quantity: number;
  lineTotal: number;
  currency: string;
}

export interface CheckoutSummary {
  subtotal: number;
  itemCount: number;
  platformFee: number;
  total: number;
  currency: string;
}

export type CheckoutPaymentType =
  | 'wallet'
  | 'card'
  | 'crypto'
  | 'credit_card'
  | 'debit_card'
  | 'mobile_money';

export interface CheckoutPaymentOption {
  type: CheckoutPaymentType;
  label: string;
  description: string;
  available: boolean;
  walletBalance?: number;
}

export type CheckoutCryptoSymbol = 'USDT' | 'USDC';
export type CheckoutCryptoChain = 'SOLANA_DEVNET' | 'BSC_TESTNET';

export interface CheckoutInitPayload {
  paymentMethod: 'card' | 'crypto';
  referralUrl?: string;
  crypto?: CheckoutCryptoSymbol;
  chain?: CheckoutCryptoChain;
  baseFiat?: string;
}

export interface CheckoutSummaryResult {
  orderItems: CheckoutOrderItem[];
  summary: CheckoutSummary;
  paymentOptions: CheckoutPaymentOption[];
}

interface GetCheckoutSummaryResponse { success: boolean; data: CheckoutSummaryResult; }
interface PlaceOrderResponse         { success: boolean; message: string; data: PlacedOrder; }
interface InitCheckoutResponse       { success: boolean; message: string; data: CheckoutInitResult; }
export interface VerifyCheckoutResponse {
  success: boolean;
  message?: string;
  data?: { orderId?: string; orderNumber?: string; paymentStatus?: string } | null;
}

export interface CheckoutInitResult {
  orderId?: string;
  orderNumber?: string;
  paymentLink?: string;
  txRef?: string;
  amount?: number;
  currency?: string;
  verifyUrl?: string;
  transactionId?: string;
  reference?: string;
  address?: string;
  network?: string;
  chain?: string;
  crypto?: string;
  baseFiat?: string;
  amountInCrypto?: number;
  platformFeeInCrypto?: number;
  amountInFiat?: number;
  validityPeriod?: number;
  expiresAt?: string;
}

// ==================== STORE SERVICE ====================

export const storeService = {

  // ========== PRODUCTS ==========

  getAllProducts: async (params: GetProductsParams = {}): Promise<ProductsResult> => {
    try {
      const { category = '', page = 1, limit = 12, sortBy = 'newest' } = params;
      const response = await apiClient.get<ProductsResponse>(ENDPOINTS.STORE.PRODUCTS, {
        params: { category, page, limit, sortBy },
      });
      if (response.data.success && response.data.data) return response.data.data;
      return { products: [], pagination: { page: 1, limit: 12, total: 0, totalPages: 0 } };
    } catch (error: unknown) {
      console.error('❌ Get products error:', error);
      throw error;
    }
  },

  getProductById: async (productId: string): Promise<Product> => {
    try {
      const response = await apiClient.get<SingleProductResponse>(
        `${ENDPOINTS.STORE.PRODUCTS}/${productId}`
      );
      if (response.data.success && response.data.data) return response.data.data;
      throw new Error('Product not found');
    } catch (error: unknown) {
      console.error('❌ Get product by ID error:', error);
      throw error;
    }
  },

  // ========== WISHLIST ==========

  getWishlist: async (): Promise<WishlistResult> => {
    try {
      const response = await apiClient.get<WishlistResponse>(ENDPOINTS.WISHLIST.GET);
      if (response.data.success && response.data.data) return response.data.data;
      return { items: [], totalItems: 0 };
    } catch (error: unknown) {
      console.error('❌ Get wishlist error:', error);
      throw error;
    }
  },

  addToWishlist: async (productId: string): Promise<WishlistItem> => {
    try {
      const response = await apiClient.post<AddToWishlistResponse>(
        ENDPOINTS.WISHLIST.ADD,
        { productId }
      );
      if (response.data.success && response.data.data?.item) return response.data.data.item;
      throw new Error(response.data.error || response.data.message || 'Failed to add to wishlist');
    } catch (error: unknown) {
      console.error('❌ Add to wishlist error:', error);
      throw error;
    }
  },

  removeFromWishlist: async (wishlistItemId: string): Promise<void> => {
    try {
      await apiClient.delete(ENDPOINTS.WISHLIST.REMOVE(wishlistItemId));
    } catch (error: unknown) {
      console.error('❌ Remove from wishlist error:', error);
      throw error;
    }
  },

  clearWishlist: async (): Promise<void> => {
    try {
      await apiClient.delete(ENDPOINTS.WISHLIST.CLEAR);
    } catch (error: unknown) {
      console.error('❌ Clear wishlist error:', error);
      throw error;
    }
  },

  // ========== CART ==========

  getCart: async (): Promise<CartResult> => {
    try {
      const response = await apiClient.get<CartResponse>(ENDPOINTS.CART.GET);
      if (response.data.success && response.data.data) return response.data.data;
      return {
        items: [],
        summary: { itemCount: 0, subtotal: 0, platformFee: 0, total: 0, currency: 'USD' },
      };
    } catch (error: unknown) {
      console.error('❌ Get cart error:', error);
      throw error;
    }
  },

  addToCart: async (data: AddToCartData): Promise<CartItem> => {
    try {
      const response = await apiClient.post<AddToCartResponse>(ENDPOINTS.CART.ADD, data);
      if (response.data.success && response.data.data?.item) return response.data.data.item;
      throw new Error(response.data.message || 'Failed to add item to cart');
    } catch (error: unknown) {
      console.error('❌ Add to cart error:', error);
      throw error;
    }
  },

  removeFromCart: async (itemId: string): Promise<void> => {
    try {
      await apiClient.delete(ENDPOINTS.CART.REMOVE(itemId));
    } catch (error: unknown) {
      console.error('❌ Remove from cart error:', error);
      throw error;
    }
  },

  updateCartItem: async (itemId: string, quantity: number): Promise<CartItem> => {
    try {
      const response = await apiClient.patch<{
        success: boolean;
        message?: string;
        data?: { item?: CartItem };
      }>(ENDPOINTS.CART.UPDATE(itemId), { quantity });
      if (response.data?.success && response.data.data?.item) return response.data.data.item;
      throw new Error(response.data?.message || 'Failed to update cart item');
    } catch (error: unknown) {
      console.error('❌ Update cart item error:', error);
      throw error;
    }
  },

  clearCart: async (): Promise<void> => {
    try {
      await apiClient.delete(ENDPOINTS.CART.CLEAR);
    } catch (error: unknown) {
      console.error('❌ Clear cart error:', error);
      throw error;
    }
  },

  // ========== CHECKOUT ==========

  // GET /operator/checkout — load items + summary + payment options
  getCheckoutSummary: async (): Promise<CheckoutSummaryResult> => {
    try {
      const response = await apiClient.get<GetCheckoutSummaryResponse>(ENDPOINTS.CHECKOUT.GET);
      if (response.data.success && response.data.data) return response.data.data;
      throw new Error('Failed to load checkout');
    } catch (error: unknown) {
      console.error('❌ Get checkout summary error:', error);
      throw error;
    }
  },

  // POST /operator/checkout - initialize card payment (returns paymentLink)
  initCheckoutPayment: async (
    payload: CheckoutInitPayload
  ): Promise<{ message: string; data: CheckoutInitResult }> => {
    try {
      const response = await apiClient.post<InitCheckoutResponse>(ENDPOINTS.CHECKOUT.CREATE, payload);
      if (response.data.success && response.data.data) {
        return { message: response.data.message, data: response.data.data };
      }
      throw new Error(response.data.message || 'Failed to initialize payment');
    } catch (error: unknown) {
      console.error('Initialize checkout payment error:', error);
      throw error;
    }
  },

  placeOrder: async (payload: {
    paymentMethod: 'wallet' | 'crypto';
    reference?: string;
  }): Promise<PlacedOrder> => {
    try {
      const response = await apiClient.post<PlaceOrderResponse>(ENDPOINTS.CHECKOUT.CREATE, payload);
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to place order');
    } catch (error: unknown) {
      console.error('Place order error:', error);
      throw error;
    }
  },

  // GET /operator/checkout/verify?transaction_id=...&tx_ref=...
  verifyCheckoutPayment: async (payload: {
    transactionId?: string;
    txRef?: string;
    reference?: string;
  }): Promise<VerifyCheckoutResponse> => {
    try {
      const searchParams = new URLSearchParams();
      if (payload.transactionId) {
        searchParams.set('transaction_id', payload.transactionId);
      }
      if (payload.txRef) {
        searchParams.set('tx_ref', payload.txRef);
      }
      if (payload.reference) {
        searchParams.set('reference', payload.reference);
      }
      const query = searchParams.toString();
      if (!query) {
        throw new Error('Missing checkout verification parameters');
      }
      const response = await apiClient.get<VerifyCheckoutResponse>(`${ENDPOINTS.CHECKOUT.VERIFY}?${query}`);
      return response.data;
    } catch (error: unknown) {
      console.error('Verify checkout payment error:', error);
      throw error;
    }
  },

  checkout: async (payload: {
    paymentMethod: 'card';
    transactionId: string;
    txRef: string;
  }): Promise<VerifyCheckoutResponse | VerifyCheckoutResponse['data']> => {
    const response = await storeService.verifyCheckoutPayment(payload);
    return response.data ?? response;
  },

  // ========== ORDERS ==========

  // GET /operator/orders?status=&page=&limit=
  getOrders: async (params: {
    status?: OrderStatus | '';
    page?: number;
    limit?: number;
  } = {}): Promise<{ orders: Order[]; pagination: Pagination }> => {
    try {
      const { status = '', page = 1, limit = 10 } = params;
      const response = await apiClient.get<OrdersListResponse>(ENDPOINTS.ORDERS.GET_ALL, {
        params: { status, page, limit },
      });
      if (response.data.success && response.data.data) return response.data.data;
      return { orders: [], pagination: { page: 1, limit: 10, total: 0, totalPages: 0 } };
    } catch (error: unknown) {
      console.error('❌ Get orders error:', error);
      throw error;
    }
  },

  // GET /operator/orders/:id
  getOrderById: async (orderId: string): Promise<OrderDetail> => {
    try {
      const response = await apiClient.get<OrderDetailResponse>(
        ENDPOINTS.ORDERS.GET_BY_ID(orderId)
      );
      if (response.data.success && response.data.data) return response.data.data;
      throw new Error('Order not found');
    } catch (error: unknown) {
      console.error('❌ Get order by ID error:', error);
      throw error;
    }
  },

  // PATCH /operator/orders/:id/cancel
  cancelOrder: async (orderId: string, reason?: string): Promise<CancelOrderResult> => {
    try {
      const response = await apiClient.patch<CancelOrderResponse>(
        ENDPOINTS.ORDERS.CANCEL(orderId),
        { reason: reason ?? '' }
      );
      if (response.data.success && response.data.data) return response.data.data;
      throw new Error('Failed to cancel order');
    } catch (error: unknown) {
      console.error('❌ Cancel order error:', error);
      throw error;
    }
  },
};

export default storeService;
